<template>
  <div class="rewards-page">

    <!-- Mobile back button -->
    <div class="mobile-header" v-if="!isDesktop">
      <button class="back-btn" @click="goBack">← ย้อนกลับ</button>
    </div>

    <div class="rewards-container">

      <!-- LEFT: Image card -->
      <div class="left-panel">
        <div class="welcome-badge">
          <img src="@/assets/icons/m-coin.svg" alt="coin" class="badge-coin" />
          <span>WELCOME GIFT<br><strong>+100 M-Coins</strong></span>
        </div>
        <div
          class="left-bg clickable"
          role="button"
          tabindex="0"
          @click="showSyncModal = true"
          @keydown.enter="showSyncModal = true"
        >
          <img src="@/assets/images/benefits.png" alt="rewards" class="hero-img" />
        </div>
        <div class="left-footer" v-if="isDesktop">
          <img src="@/assets/images/big-logo.png" alt="logo" class="footer-logo" />
          <p class="footer-tag">PREMIUM MEMBERSHIP</p>
          <p class="footer-text">Join 20,000+ members<br>enjoying exclusive care.</p>
        </div>
      </div>

      <!-- MOBILE: actions right under the image (desktop keeps them in the right panel) -->
      <div class="action-row action-row--mobile" v-if="!isDesktop">
        <button class="btn-signin" @click="showSyncModal = true">
          <img src="@/assets/icons/white-line.svg" alt="line" class="btn-line-icon" />
          Sign In / Register →
        </button>
      </div>

      <!-- RIGHT: Benefits -->
      <div class="right-panel">
        <h1 class="rewards-title">The Rewards of<br><em>Healthy Skin.</em></h1>
        <p class="rewards-desc">
          Our membership program is designed to reward your<br>
          commitment to skincare. Sync your profile to unlock a<br>
          world of benefits.
        </p>

        <div class="benefits-list">
          <div class="benefit-item">
            <div class="benefit-icon">
              <img src="@/assets/icons/m-coin.svg" alt="coin" />
            </div>
            <div class="benefit-text">
              <p class="benefit-title">Collect M-Coins</p>
              <p class="benefit-desc">Earn coins for every purchase, treatment, and clinic visit. 10 Baht = 1 M-Coin.</p>
            </div>
          </div>
          <div class="benefit-item">
            <div class="benefit-icon">🎟️</div>
            <div class="benefit-text">
              <p class="benefit-title">Redeem Anywhere</p>
              <p class="benefit-desc">Use your coins to unlock coupons valid both in our Online shop and Offline clinic branches.</p>
            </div>
          </div>
          <div class="benefit-item">
            <div class="benefit-icon">🎂</div>
            <div class="benefit-text">
              <p class="benefit-title">Birth Month Special</p>
              <p class="benefit-desc">Enjoy a 5% discount throughout your entire birth month with no maximum limit on savings.</p>
            </div>
          </div>
        </div>

        <div class="action-row" v-if="isDesktop">
          <button class="btn-signin" @click="showSyncModal = true">
            <img src="@/assets/icons/white-line.svg" alt="line" class="btn-line-icon" />
            Sign In / Register →
          </button>
        </div>
      </div>

      <!-- MOBILE: membership footer at the very bottom (desktop keeps it in the left panel) -->
      <div class="left-footer left-footer--mobile" v-if="!isDesktop">
        <img src="@/assets/images/big-logo.png" alt="logo" class="footer-logo" />
        <p class="footer-tag">PREMIUM MEMBERSHIP</p>
        <p class="footer-text">Join 20,000+ members<br>enjoying exclusive care.</p>
      </div>

    </div>

    <!-- SyncHistoryModal แสดงหลัง LINE login สำเร็จ -->
    <SyncHistoryModal
      :visible="showSyncModal"
      :from-path="fromPath"
      success-path="/my-profile"
      :is-line-authenticating="isLineAuthenticating"
      :line-auth-error="lineAuthError"
      :line-auth-completed-at="lineAuthCompletedAt"
      @close="showSyncModal = false"
      @success="showSyncModal = false"
      @start-line-auth="startSyncFlow"
    />

  </div>
</template>

<script>
import { onMounted, ref } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useStore } from 'vuex'
import { useScreenSize } from '@/utils/isDesktop'

import SyncHistoryModal from '@/components/SyncHistoryModal'

export default {
  name: 'SyncRewardsPage',
  components: { SyncHistoryModal },

  setup() {
    const router    = useRouter()
    const route     = useRoute()
    const store = useStore()
    const { isDesktop } = useScreenSize()
    const showSyncModal = ref(false)
    const getStoredFromPath = () => sessionStorage.getItem('line_sync_from') || '/my-cart'
    const fromPath = route.query.from || getStoredFromPath()
    const isLineAuthenticating = ref(false)
    const lineAuthError = ref('')
    const lineAuthCompletedAt = ref(0)
    const LINE_SYNC_PENDING_KEY = 'line_sync_pending'
    const LINE_SYNC_FROM_KEY = 'line_sync_from'
    const LINE_SYNC_TOKEN_KEY = 'line_sync_access_token'

    const goBack = () => {
      const from = route.query.from
      if (from) router.push(from)
      else router.back()
    }

    const getCouponSelectionPath = () => ({
      path: '/my-coupons',
    })

    const clearPendingLineSync = () => {
      sessionStorage.removeItem(LINE_SYNC_PENDING_KEY)
      sessionStorage.removeItem(LINE_SYNC_FROM_KEY)
    }

    const attachLineToCurrentUser = async (lineAccessToken) => {
      await store.dispatch('user/fetchLoginWithLine', {
        lineAccessToken,
        attachCurrentUser: true,
      })
      await store.dispatch('user/fetchGetUserProfile')
    }

    const finalizeLineLogin = async (lineAccessToken, { attachLine = false } = {}) => {
      // This flow decides its own destination below; drop any stale 'redirectTo' so the
      // profile fetch can't hard-redirect (window.location.href) back to /sync-rewards.
      sessionStorage.removeItem('redirectTo')
      if (attachLine) {
        await attachLineToCurrentUser(lineAccessToken)
        sessionStorage.removeItem(LINE_SYNC_TOKEN_KEY)
        showSyncModal.value = false
        router.replace(getCouponSelectionPath())
        return
      }

      const response = await store.dispatch('user/fetchCheckLineLoginStatus', {
        lineAccessToken,
      })
      sessionStorage.setItem(LINE_SYNC_TOKEN_KEY, lineAccessToken)

      if (response?.should_skip_phone === true && response?.access) {
        store.commit('user/SET_USER', response)
        await store.dispatch('user/fetchGetUserProfile')
        showSyncModal.value = false
        sessionStorage.removeItem(LINE_SYNC_TOKEN_KEY)
        clearPendingLineSync()
        router.replace(getCouponSelectionPath())
        return
      }

      lineAuthCompletedAt.value = Date.now()
      showSyncModal.value = true
      clearPendingLineSync()
    }

    const startSyncFlow = async ({ attachLine = false, isResume = false } = {}) => {
      lineAuthError.value = ''
      // Already authenticated (e.g. logged in via LINE but still missing phone/clinic id):
      // skip the LINE OAuth round-trip entirely and open the phone/register step directly
      // in the already-open modal. This avoids the fragile isInClient=false OAuth redirect
      // (which buries the path in liff.state and loses the token across the round-trip).
      if (!attachLine && !isResume && store.getters['user/getUser']) {
        clearPendingLineSync()
        sessionStorage.removeItem(LINE_SYNC_TOKEN_KEY)
        lineAuthCompletedAt.value = Date.now()
        showSyncModal.value = true
        return
      }
      isLineAuthenticating.value = true
      try {
        sessionStorage.setItem(LINE_SYNC_PENDING_KEY, '1')
        sessionStorage.setItem(LINE_SYNC_FROM_KEY, fromPath)
        const lineAccessToken = sessionStorage.getItem(LINE_SYNC_TOKEN_KEY)
        if (!lineAccessToken) {
          if (isResume) {
            lineAuthError.value = 'ไม่สามารถเชื่อมต่อ LINE ได้ กรุณาลองใหม่อีกครั้ง'
            clearPendingLineSync()
            return
          }

          const query = attachLine ? '?path=sync-rewards&attach_line=1' : '?path=sync-rewards'
          router.push(`/user/login${query}`)
          return
        }

        await finalizeLineLogin(lineAccessToken, { attachLine })
        return
      } catch (err) {
        console.log(err)
        lineAuthError.value = 'ไม่สามารถเชื่อมต่อ LINE ได้ กรุณาลองใหม่อีกครั้ง'
        if (attachLine) {
          showSyncModal.value = true
        }
        clearPendingLineSync()
      } finally {
        isLineAuthenticating.value = false
      }
    }

    onMounted(async () => {
      if (sessionStorage.getItem(LINE_SYNC_PENDING_KEY) === '1') {
        await startSyncFlow({ isResume: true })
      }
    })

    return {
      isDesktop,
      showSyncModal,
      goBack,
      fromPath,
      startSyncFlow,
      isLineAuthenticating,
      lineAuthError,
      lineAuthCompletedAt,
    }
  }
}
</script>

<style lang="scss" scoped>

.rewards-page {
  height: 100%;
  background: #F8F7F4;
  display: flex;
  flex-direction: column;
  overflow-y: auto;

  @media screen and (min-width: 780px) {
    min-height: calc(100vh - 74px);
    padding: 36px 32px 48px;
    justify-content: center;
  }
}

// Mobile header
.mobile-header {
  padding: 16px;
  background: #fff;
  border-bottom: 1px solid #eee;
}
.back-btn {
  background: none; border: none; color: var(--secondary-color, #4A58E5);
  font-size: 15px; cursor: pointer; font-family: 'NotoSansThai', sans-serif;
}

// Container
.rewards-container {
  display: flex;
  flex-direction: column;
  flex: 1;

  @media screen and (min-width: 780px) {
    flex-direction: row;
    flex: 0 1 auto;
    width: min(1180px, 100%);
    min-height: min(720px, calc(100vh - 180px));
    margin: 0 auto;
    border-radius: 30px;
    overflow: hidden;
    background: #fff;
    box-shadow: 0 24px 60px rgba(28, 33, 63, 0.10);
  }
}

// ── LEFT PANEL ───────────────────────────────────────────────────────────────
.left-panel {
  position: relative;
  background: #fff;
  min-height: 280px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  overflow: hidden;

  @media screen and (min-width: 780px) {
    width: 43%;
    min-height: 720px;
    padding: 24px;
  }
}

.welcome-badge {
  position: absolute;
  top: 20px;
  right: 20px;
  background: #fff;
  border-radius: 12px;
  padding: 10px 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.15);
  z-index: 2;
  font-size: 12px;
  line-height: 1.4;
  color: #1A1A2E;
  strong { font-size: 14px; color: #6D2E46; }
}
.badge-coin { width: 28px; height: 28px; }

.hero-img {
  display: block;
  width: 100%;
  height: auto;
  max-height: 100%;
  object-fit: contain;
  opacity: 1;
}

.left-bg {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  min-height: 0;
}
.left-bg.clickable {
  cursor: pointer;
}

.left-footer {
  position: relative;
  z-index: 2;
  width: 100%;
  padding: 18px 4px 0;
  color: #1A1A2E;
  text-align: left;

  @media screen and (min-width: 780px) {
    padding: 20px 8px 4px;
  }
}
.footer-logo { width: 54px; margin-bottom: 8px; }
.footer-tag  { font-size: 11px; letter-spacing: 2px; opacity: 0.65; margin: 0 0 8px; }
.footer-text {
  font-size: 18px;
  font-weight: 700;
  line-height: 1.35;
  margin: 0;

  @media screen and (min-width: 780px) {
    max-width: 280px;
  }
}

// ── RIGHT PANEL ──────────────────────────────────────────────────────────────
.right-panel {
  flex: 1;
  padding: 32px 28px 48px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  background: #fff;

  @media screen and (min-width: 780px) {
    padding: 44px 56px;
  }
}

.rewards-title {
  font-size: 32px;
  font-weight: bold;
  color: #1A1A2E;
  line-height: 1.25;
  margin: 0 0 16px;

  em {
    font-style: italic;
    color: #6D2E46;
    font-weight: 300;
  }

  @media screen and (min-width: 780px) { font-size: 38px; }
}

.rewards-desc {
  font-size: 14px;
  color: #666;
  line-height: 1.7;
  margin: 0 0 32px;
}

// Benefits
.benefits-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 36px;

  @media screen and (min-width: 780px) {
    gap: 18px;
    margin-bottom: 32px;
  }
}

.benefit-item {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.benefit-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: #F5F0FF;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 20px;

  > img { width: 22px; height: 22px; }
}

.benefit-title {
  font-size: 15px;
  font-weight: bold;
  color: #1A1A2E;
  margin: 0 0 4px;
}
.benefit-desc {
  font-size: 13px;
  color: #666;
  line-height: 1.6;
  margin: 0;
}

// Action buttons
.action-row {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

// Mobile-only: buttons sit directly under the image, above the rewards text
.action-row--mobile {
  background: #fff;
  padding: 20px 28px 24px;
  justify-content: center;
}

// Mobile-only: membership footer pinned to the very bottom, centered
.left-footer--mobile {
  background: #fff;
  padding: 8px 28px 32px;
  text-align: center;

  .footer-logo { margin-left: auto; margin-right: auto; }
  .footer-text { margin-left: auto; margin-right: auto; }
}

.btn-signin {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 28px;
  background: #1A1A2E;
  color: #fff;
  border: none;
  border-radius: 50px;
  font-size: 15px;
  font-weight: bold;
  cursor: pointer;
  font-family: 'NotoSansThai', sans-serif;
  transition: opacity 0.2s;
  &:hover { opacity: 0.85; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
}
.btn-line-icon { width: 20px; height: 20px; }

.btn-back {
  background: none;
  border: none;
  color: #888;
  font-size: 14px;
  cursor: pointer;
  font-family: 'NotoSansThai', sans-serif;
  text-decoration: underline;
  &:hover { color: #333; }
}

.line-auth-error {
  margin-top: 12px;
  color: #DC2626;
  font-size: 13px;
}
</style>
